/**
 * Offline photo queue for construction module.
 *
 * Design: photos are saved to IndexedDB the moment they're captured.
 * If the upload succeeds online, the queue entry is deleted.
 * If offline, the entry stays. A background sync attempts uploads
 * whenever the browser comes back online.
 *
 * Each queue entry has:
 *   - id                random uuid
 *   - endpoint          e.g. '/projects/.../bus'
 *   - fields            { direction, date, lat, lng, ... }
 *   - blob              actual image Blob
 *   - createdAt         epoch ms
 *   - attempts          retry counter
 *   - lastError         message
 */
const DB_NAME = 'alf_offline_v1';
const STORE = 'photo_queue';

interface QueueEntry {
  id: string;
  endpoint: string;
  fields: Record<string, string>;
  blob: Blob;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function uuid() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'q_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
}

export async function enqueuePhoto(input: {
  endpoint: string;
  fields: Record<string, string>;
  blob: Blob;
}): Promise<string> {
  const db = await openDb();
  const id = uuid();
  const entry: QueueEntry = {
    id,
    endpoint: input.endpoint,
    fields: input.fields,
    blob: input.blob,
    createdAt: Date.now(),
    attempts: 0,
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = () => resolve(id);
    tx.onerror = () => reject(tx.error);
  });
}

export async function listQueue(): Promise<QueueEntry[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as QueueEntry[]);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteFromQueue(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function updateQueue(entry: QueueEntry): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function queueCount(): Promise<number> {
  const items = await listQueue();
  return items.length;
}

/**
 * Attempt to upload a single queue entry.
 * Returns true on success (entry is deleted), false otherwise (entry retained).
 */
export async function tryUpload(entry: QueueEntry, baseUrl: string, jwt: string): Promise<boolean> {
  try {
    const fd = new FormData();
    for (const [k, v] of Object.entries(entry.fields)) fd.append(k, v);
    fd.append('file', entry.blob, `photo_${entry.id}.jpg`);
    const res = await fetch(`${baseUrl}${entry.endpoint}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${jwt}` },
      body: fd,
    });
    if (res.ok) {
      await deleteFromQueue(entry.id);
      return true;
    }
    entry.attempts += 1;
    entry.lastError = `HTTP ${res.status}`;
    await updateQueue(entry);
    return false;
  } catch (e: any) {
    entry.attempts += 1;
    entry.lastError = e?.message ?? 'network error';
    await updateQueue(entry);
    return false;
  }
}

/**
 * Drain the queue: try every entry, in order. Stop at first failure to
 * avoid hammering on a known bad network.
 */
export async function drainQueue(baseUrl: string, jwt: string): Promise<{
  uploaded: number; remaining: number;
}> {
  const items = await listQueue();
  let uploaded = 0;
  for (const it of items) {
    const ok = await tryUpload(it, baseUrl, jwt);
    if (ok) uploaded += 1;
    else break;
  }
  const remaining = (await listQueue()).length;
  return { uploaded, remaining };
}

/**
 * Smart capture: try to upload immediately. If offline or the request
 * fails, enqueue. Returns 'sent' or 'queued'.
 */
export async function captureAndUpload(input: {
  endpoint: string;
  fields: Record<string, string>;
  blob: Blob;
  baseUrl: string;
  jwt: string;
}): Promise<{ status: 'sent' | 'queued'; id?: string; result?: any }> {
  if (navigator.onLine) {
    try {
      const fd = new FormData();
      for (const [k, v] of Object.entries(input.fields)) fd.append(k, v);
      fd.append('file', input.blob, 'photo.jpg');
      const res = await fetch(`${input.baseUrl}${input.endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${input.jwt}` },
        body: fd,
      });
      if (res.ok) {
        const result = await res.json();
        return { status: 'sent', result };
      }
    } catch { /* fall through to queue */ }
  }
  const id = await enqueuePhoto(input);
  return { status: 'queued', id };
}

/**
 * Attempt best-effort GPS lookup with short timeout. Never throws.
 * Returns null if denied, unsupported, or slow.
 */
export function tryGetLocation(timeoutMs = 4000): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    const timer = setTimeout(() => resolve(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}
